import React from "react";

/**
 * A screen's selection or open section, held in the URL when there is a router above it and in
 * component state when there is not (#1228).
 *
 * The reason for the second half: these screens are rendered directly by their unit tests, which
 * mount the component and click through it without a `RouterProvider`. Making the route the only
 * source would have meant wrapping every one of those tests in routing machinery they are not
 * testing. So the prop pair is optional, and a screen given neither behaves exactly as it did
 * before — same defaults, same interactions.
 *
 * Only one of the two is ever read: `routed` decides, and the local value exists only while
 * `onChange` is absent. That is what keeps this from being two sources of truth.
 */
export function useRoutedState<T>(
  routed: T | undefined,
  onChange: ((next: T) => void) | undefined,
  fallback: T,
): [T, (next: T) => void] {
  const [local, setLocal] = React.useState<T>(fallback);
  const controlled = onChange !== undefined;
  const value = controlled ? routed ?? fallback : local;
  const set = React.useCallback((next: T) => {
    if (onChange) onChange(next);
    else setLocal(next);
  }, [onChange]);
  return [value, set];
}
